import { CSSProperties } from 'react';
import type { MaintenanceInfo } from 'utils/maintenance';

type MaintenanceNoticeProps = {
  info: MaintenanceInfo;
  onBack: () => void;
};

/**
 * Full-screen "game closed for maintenance" notice (Unity parity —
 * AuthenticationView → MaintenanceView). Shown instead of entering the app
 * when fetchGameCloseMaintenance reports an active window, right after a
 * successful login. Shared by LoginScreen (web's embedded Descope flow) and
 * useNativeLoginDeepLink (native's deep-link callback), so both surfaces show
 * the exact same notice.
 */
const MaintenanceNotice = ({ info, onBack }: MaintenanceNoticeProps) => {
  const title = (info.title || '').trim() || 'Under Maintenance';
  const detail =
    (info.displayMessage || '').trim() ||
    (info.endDatetime
      ? `The game is closed for maintenance. Server up by ${info.endDatetime}.`
      : 'The game is temporarily closed for maintenance. Please try again later.');

  return (
    <div style={containerStyle}>
      <div style={maintenanceContainer}>
        <span style={maintenanceIcon} aria-hidden='true'>
          <svg viewBox='0 0 24 24' width='44' height='44' fill='currentColor'>
            <path d='M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z' />
          </svg>
        </span>
        <div style={maintenanceTitle}>{title}</div>
        <div style={maintenanceMessage}>{detail}</div>
        <button onClick={onBack} style={maintenanceBtn}>
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default MaintenanceNotice;

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  backgroundColor: '#12100c',
  color: 'white',
  overflow: 'auto'
};

const maintenanceContainer: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1rem',
  padding: '0 2rem',
  maxWidth: '420px',
  textAlign: 'center'
};

const maintenanceIcon: CSSProperties = {
  color: '#E98C26',
  display: 'grid',
  placeItems: 'center'
};

const maintenanceTitle: CSSProperties = {
  fontSize: '1.4rem',
  fontWeight: 800,
  color: '#ffffff',
  fontFamily: "'Rye', 'Outfit', serif",
  letterSpacing: '0.02em'
};

const maintenanceMessage: CSSProperties = {
  fontSize: '0.9rem',
  lineHeight: 1.5,
  color: '#ffffff',
  marginBottom: '0.5rem'
};

const loginBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '300px',
  padding: '1rem 2rem',
  background: 'linear-gradient(135deg, #d4a853 0%, #b8912e 100%)',
  border: 'none',
  borderRadius: '12px',
  color: '#1a1408',
  fontSize: '1.1rem',
  fontWeight: 700,
  fontFamily: "'Rye', 'Outfit', serif",
  cursor: 'pointer',
  minHeight: '56px',
  boxShadow: '0 4px 16px rgba(212, 168, 83, 0.3)',
  letterSpacing: '0.02em'
};

// Uses the app's orange theme (saloonTokens.orange400 → orange500) instead of
// the gold login button so the maintenance CTA reads as an app-themed action.
const maintenanceBtn: CSSProperties = {
  ...loginBtn,
  background: 'linear-gradient(135deg, #f59838 0%, #e8821f 100%)',
  color: '#1a0e06',
  boxShadow: '0 4px 16px rgba(232, 130, 31, 0.35)'
};
